/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useEffect, useState } from "react";
import { Button } from "@vector-im/compound-web";

import { useSetting } from "./settings";
import { defaultShortcuts, shortcuts, type ShortcutMap } from "../meet/MeetSettings";
import styles from "./MeetSettings.module.css";

const LABELS: Record<keyof ShortcutMap, string> = {
  toggleMute: "Mute / unmute microphone",
  toggleCamera: "Toggle camera",
  toggleScreenShare: "Toggle screen share",
  toggleRecording: "Start / stop recording",
  raiseHand: "Raise / lower hand",
  pushToTalk: "Push to talk (hold)",
  toggleWhiteboard: "Toggle whiteboard",
  togglePiP: "Picture-in-Picture",
};

export const KeyboardSettingsTab: FC = () => {
  const [map, setMap] = useSetting(shortcuts);
  const [recording, setRecording] = useState<keyof ShortcutMap | null>(null);

  useEffect(() => {
    if (!recording) return;
    const handler = (e: KeyboardEvent): void => {
      e.preventDefault();
      setMap({ ...map, [recording]: e.key.toLowerCase() });
      setRecording(null);
    };
    window.addEventListener("keydown", handler, { once: true });
    return () => window.removeEventListener("keydown", handler);
  }, [recording, map, setMap]);

  return (
    <div>
      <h3 className={styles.sectionTitle}>Keyboard Shortcuts</h3>
      <p className={styles.small}>
        Click “Record” then press the key you want to assign. Stored locally.
      </p>
      {(Object.keys(LABELS) as (keyof ShortcutMap)[]).map((key) => (
        <div key={key} className={styles.row}>
          <span>{LABELS[key]}</span>
          <span>
            <code style={{ marginRight: 8 }}>{map[key]}</code>
            <Button onClick={() => setRecording(key)}>
              {recording === key ? "Press a key…" : "Record"}
            </Button>
          </span>
        </div>
      ))}
      <Button onClick={() => setMap(defaultShortcuts)}>Reset to defaults</Button>
    </div>
  );
};
